import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOSchema from '@/components/feature/SEOSchema';
import { categorySubCategories } from '@/mocks/data';
import { allComics } from '@/mocks/data';
import { categories } from '@/mocks/home';

export default function CategoryPage() {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const catData = slug ? categorySubCategories[slug] : null;

  if (!catData) {
    return (
      <>
        <SEOSchema type="website" title="分类未找到 - 韩漫屋" url={`http://fkyy11.com/category/${slug}`} />
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center bg-background-50">
          <div className="text-center">
            <i className="ri-emotion-sad-line text-5xl text-foreground-300 mb-4 block"></i>
            <p className="text-foreground-500 text-lg">{t('common:noCategoryFound')}</p>
            <Link to="/" className="mt-4 inline-block text-primary-500 hover:text-primary-600 cursor-pointer">
              {t('common:breadcrumbHome')}
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categoryComics = allComics.filter((c) => c.slug === slug);
  const mainCat = categories.find((c) => c.slug === slug);

  return (
    <>
      <SEOSchema
        type="website"
        title={`${catData.title} - 韩漫屋韩漫分类`}
        description={catData.description}
        url={`http://fkyy11.com/category/${slug}`}
        breadcrumbs={[
          { name: t('common:breadcrumbHome'), url: 'http://fkyy11.com/' },
          { name: t('common:navCategory'), url: 'http://fkyy11.com/category/action' },
          { name: catData.title, url: `http://fkyy11.com/category/${slug}` },
        ]}
      />

      <Navbar />

      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: t('common:breadcrumbHome'), href: '/' },
            { label: t('common:navCategory'), href: '/category/action' },
            { label: catData.title },
          ]}
        />

        <section className="w-full py-10 md:py-14 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
              <div className="lg:w-2/5">
                <div className="relative rounded-lg overflow-hidden aspect-[3/2]">
                  <img
                    src={catData.image}
                    alt={catData.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-foreground-950/35"></div>
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-6">
                    <h1 className="text-3xl md:text-4xl font-heading font-bold text-background-50 mb-2">
                      {catData.title}
                    </h1>
                    <p className="text-sm text-background-50/70">
                      {mainCat ? `${mainCat.count} ` : ''}{t('common:categoryTotalWorks')}
                    </p>
                  </div>
                </div>
              </div>

              <div className="lg:w-3/5 flex flex-col justify-center">
                <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-6">
                  {catData.description}
                </p>
                <div>
                  <h2 className="text-sm font-bold text-foreground-400 uppercase tracking-wider mb-3">
                    {t('common:categorySubTitle')}
                  </h2>
                  <div className="flex flex-wrap gap-2">
                    {catData.subCategories.map((sub) => (
                      <Link
                        key={sub.id}
                        to={`/category/${slug}`}
                        className="px-4 py-2 bg-background-100 border border-background-200 rounded-full text-sm text-foreground-600 hover:bg-primary-50 hover:text-primary-600 hover:border-primary-200 transition-all duration-200 whitespace-nowrap cursor-pointer"
                      >
                        {sub.name}
                        <span className="ml-1.5 text-xs text-foreground-400">({sub.count})</span>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full py-14 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <div className="flex items-end justify-between mb-8 md:mb-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                  {t('common:categoryLatestTitle')}
                </h2>
                <p className="text-sm text-foreground-500 mt-1">{catData.title}分类下的最新漫画推荐</p>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {categoryComics.map((comic) => (
                <Link
                  key={comic.id}
                  to={`/article/${comic.id}`}
                  className="group cursor-pointer"
                >
                  <div className="relative overflow-hidden rounded-lg bg-background-200">
                    <div className="relative w-full aspect-[5/7] overflow-hidden">
                      <img
                        src={comic.cover}
                        alt={comic.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                      <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-foreground-950/70 to-transparent"></div>
                      <div className="absolute bottom-3 left-3 right-3">
                        <div className="flex items-center gap-1 text-background-50 text-xs">
                          <i className="ri-star-fill text-accent-400 text-xs"></i>
                          <span>{comic.rating}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 px-1">
                    <h3 className="text-sm md:text-base font-medium text-foreground-900 truncate group-hover:text-primary-600 transition-colors">
                      {comic.title}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-1">
                      {comic.author} · {comic.chapters}{t('common:chapters')}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full py-16 md:py-24 bg-background-50 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 left-1/4 w-72 h-72 bg-primary-100/40 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 right-1/4 w-72 h-72 bg-accent-100/30 rounded-full blur-3xl"></div>
          </div>
          <div className="relative z-10 w-full px-4 md:px-6 text-center max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 leading-tight">
              订阅{catData.title}更新
            </h2>
            <p className="mt-4 md:mt-5 text-sm md:text-base text-foreground-500 max-w-xl mx-auto leading-relaxed">
              第一时间获取{catData.title}分类的最新漫画更新通知，不再错过任何精彩内容
            </p>
            <div className="mt-7 md:mt-9">
              <Link
                to="/category/action"
                className="inline-flex items-center gap-2 px-8 py-3.5 bg-primary-500 text-background-50 rounded-full font-medium text-sm md:text-base hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                {t('common:ctaButton')}
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}